<div class="font-serif text-gray-900 min-h-screen bg-white">

  <!-- Breadcrumb -->
  <div class="bg-[#faf8f5] border-b border-gray-200 px-3 sm:px-6 md:px-12 py-2.5">
    <div class="max-w-[1100px] mx-auto flex items-center gap-2 text-[10px] sm:text-xs">
      <button (click)="router.navigate(['/'])" class="flex items-center gap-1 text-gray-600 hover:text-[#600b0b] font-bold uppercase tracking-[0.1em] transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
        Back
      </button>
      <span class="text-gray-300">|</span>
      <span class="text-gray-500">Custom Wallpaper</span>
      <span class="text-gray-300">/</span>
      <span class="text-[#600b0b] font-bold uppercase tracking-[0.1em]">Upload Design</span>
    </div>
  </div>

  <!-- Main Content -->
  <div class="max-w-[1100px] mx-auto px-3 sm:px-6 md:px-12 py-5 sm:py-8">

    <!-- Title -->
    <div class="mb-5 sm:mb-6">
      <h1 class="text-2xl sm:text-3xl font-serif font-medium text-gray-900 mb-1">Upload Your Design</h1>
      <p class="text-xs sm:text-sm text-gray-500">Upload your wallpaper file and we'll print it directly on your chosen surface.</p>
    </div>

    <!-- Two Column Layout -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">

      <!-- ═══ LEFT COLUMN ═══ -->
      <div class="flex flex-col gap-4 sm:gap-5">

        <!-- Upload Area / Preview (normal mode) -->
        <div *ngIf="!isCropping"
          (drop)="onDrop($event)" (dragover)="onDragOver($event)" (dragleave)="onDragLeave()"
          [class]="'border-2 border-dashed rounded-xl transition-all min-h-[200px] sm:min-h-[260px] ' + (isDragging ? 'border-[#600b0b] bg-[#600b0b]/5' : 'border-gray-300 bg-[#faf8f5]')"
        >
          <!-- Empty state -->
          <div *ngIf="!previewUrl" (click)="fileInput.click()"
            class="flex flex-col items-center justify-center text-center cursor-pointer p-6 sm:p-8 min-h-[200px] sm:min-h-[260px]">
            <input #fileInput type="file" accept="image/jpeg,image/png" (change)="onFileSelected($event)" class="hidden" />
            <div class="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8 sm:w-10 sm:h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" class="text-gray-400">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </div>
            <p class="font-serif font-medium text-gray-700 text-sm mb-1">Click to upload your design</p>
            <p class="text-[10px] sm:text-xs text-gray-400">JPG, PNG — Max 50MB</p>
          </div>

          <!-- Preview state -->
          <div *ngIf="previewUrl" class="relative p-3 sm:p-4">
            <input #fileInput type="file" accept="image/jpeg,image/png" (change)="onFileSelected($event)" class="hidden" />
            <img [src]="previewUrl" alt="Preview" class="w-full max-h-[220px] sm:max-h-[260px] object-contain rounded-lg" />
            <button (click)="removeFile()" class="absolute top-2 right-2 sm:top-3 sm:right-3 w-7 h-7 sm:w-8 sm:h-8 bg-[#600b0b]/80 hover:bg-[#600b0b] text-white rounded-full flex items-center justify-center shadow-md transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3 sm:w-3.5 sm:h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <div *ngIf="selectedFile" class="absolute bottom-4 left-4 sm:bottom-5 sm:left-5 bg-black/60 text-white text-[8px] sm:text-[10px] font-bold uppercase tracking-wider px-2 sm:px-3 py-1 rounded-md flex items-center gap-2">
              <span class="truncate max-w-[140px] sm:max-w-[200px]">{{ selectedFile.name }}</span>
              <span *ngIf="imageInfo" class="text-white/70">{{ imageInfo.width }} &times; {{ imageInfo.height }}</span>
            </div>
          </div>
        </div>

        <!-- ═══ IN-PLACE CROP MODE ═══ -->
        <div *ngIf="isCropping" class="flex flex-col gap-3">
          <p class="text-xs text-gray-500">Click and drag to select the crop area, then press Apply.</p>

          <div #cropContainer class="relative select-none bg-gray-100 rounded-xl overflow-hidden border-2 border-[#600b0b]" style="touch-action: none;">
            <img [src]="originalPreviewUrl" alt="Crop preview" class="w-full max-h-[240px] sm:max-h-[300px] object-contain block" draggable="false" />

            <div class="absolute inset-0 pointer-events-none">
              <div class="absolute bg-black/50" [style.top]="'0'" [style.left]="'0'" [style.right]="'0'" [style.height]="cropBox.y + '%'"></div>
              <div class="absolute bg-black/50" [style.bottom]="'0'" [style.left]="'0'" [style.right]="'0'" [style.height]="(100 - cropBox.y - cropBox.h) + '%'"></div>
              <div class="absolute bg-black/50" [style.top]="cropBox.y + '%'" [style.left]="'0'" [style.width]="cropBox.x + '%'" [style.height]="cropBox.h + '%'"></div>
              <div class="absolute bg-black/50" [style.top]="cropBox.y + '%'" [style.right]="'0'" [style.width]="(100 - cropBox.x - cropBox.w) + '%'" [style.height]="cropBox.h + '%'"></div>
            </div>

            <div class="absolute cursor-move"
              [style.top]="cropBox.y + '%'" [style.left]="cropBox.x + '%'"
              [style.width]="cropBox.w + '%'" [style.height]="cropBox.h + '%'"
              (mousedown)="onCropHandleMouseDown($event, 'move')"
              (touchstart)="onCropHandleMouseDown($event, 'move')">
              <div class="absolute inset-0 border-2 border-dashed border-white pointer-events-none"></div>
              <div class="absolute -top-[5px] -left-[5px] w-2.5 h-2.5 bg-white rounded-full shadow cursor-nwse-resize z-10 border border-gray-300"
                (mousedown)="onCropHandleMouseDown($event, 'tl')" (touchstart)="onCropHandleMouseDown($event, 'tl')"></div>
              <div class="absolute -top-[5px] -right-[5px] w-2.5 h-2.5 bg-white rounded-full shadow cursor-nesw-resize z-10 border border-gray-300"
                (mousedown)="onCropHandleMouseDown($event, 'tr')" (touchstart)="onCropHandleMouseDown($event, 'tr')"></div>
              <div class="absolute -bottom-[5px] -left-[5px] w-2.5 h-2.5 bg-white rounded-full shadow cursor-nesw-resize z-10 border border-gray-300"
                (mousedown)="onCropHandleMouseDown($event, 'bl')" (touchstart)="onCropHandleMouseDown($event, 'bl')"></div>
              <div class="absolute -bottom-[5px] -right-[5px] w-2.5 h-2.5 bg-white rounded-full shadow cursor-nwse-resize z-10 border border-gray-300"
                (mousedown)="onCropHandleMouseDown($event, 'br')" (touchstart)="onCropHandleMouseDown($event, 'br')"></div>
            </div>
          </div>

          <div class="flex gap-2">
            <button (click)="applyCrop()"
              class="flex-1 flex items-center justify-center gap-1.5 bg-[#600b0b] text-white py-2.5 rounded-lg font-bold uppercase tracking-[0.1em] text-[10px] sm:text-xs hover:bg-[#8c1c1c] transition-all shadow">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6.13 1L6 16a2 2 0 0 0 2 2h15"/><path d="M1 6.13L16 6a2 2 0 0 1 2 2v15"/></svg>
              Apply Crop
            </button>
            <button (click)="cancelCrop()"
              class="px-5 py-2.5 border border-gray-300 rounded-lg text-[10px] sm:text-xs font-bold uppercase tracking-[0.1em] text-gray-600 hover:border-gray-500 transition-all bg-white">
              Cancel
            </button>
          </div>
        </div>

        <!-- Image Details -->
        <div *ngIf="imageInfo && !isCropping" class="border border-gray-200 rounded-xl p-4 sm:p-5">
          <div class="flex items-center gap-2 mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-[#600b0b]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <h3 class="text-xs font-bold uppercase tracking-[0.1em] text-gray-800">Image Details</h3>
          </div>

          <div class="grid grid-cols-3 gap-3 mb-3">
            <div>
              <p class="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-0.5">Resolution</p>
              <p class="text-xs sm:text-sm font-medium text-gray-800">{{ imageInfo.width }} &times; {{ imageInfo.height }}</p>
            </div>
            <div>
              <p class="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-0.5">Aspect Ratio</p>
              <p class="text-xs sm:text-sm font-medium text-gray-800">{{ imageInfo.aspectRatio }}</p>
            </div>
            <div>
              <p class="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-0.5">Max Print @150DPI</p>
              <p class="text-xs sm:text-sm font-medium text-gray-800">{{ imageInfo.printWidthFt }} &times; {{ imageInfo.printHeightFt }} ft</p>
            </div>
          </div>

          <div *ngIf="isLowResolution"
            class="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-amber-500 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <div>
              <p class="text-xs font-bold text-amber-700">Low Resolution</p>
              <p class="text-[10px] text-amber-600 leading-relaxed">
                Effective DPI is {{ effectiveDpi }}. Consider upscaling or using a higher resolution image.
              </p>
            </div>
          </div>
        </div>

        <!-- Crop / Resize / Upscale Buttons -->
        <div *ngIf="previewUrl && !isCropping" class="flex flex-wrap gap-2">
          <button (click)="openCrop()" class="flex items-center gap-1.5 px-3 sm:px-4 py-2 border border-gray-200 rounded-lg text-[10px] sm:text-xs font-bold uppercase tracking-[0.1em] text-gray-600 hover:border-gray-400 hover:text-gray-800 transition-all bg-white">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6.13 1L6 16a2 2 0 0 0 2 2h15"/><path d="M1 6.13L16 6a2 2 0 0 1 2 2v15"/></svg>
            Crop
          </button>
          <button (click)="openResize()"
            [class]="'flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-[10px] sm:text-xs font-bold uppercase tracking-[0.1em] transition-all border ' + (isResizing ? 'bg-[#600b0b] text-white border-[#600b0b]' : 'border-gray-200 text-gray-600 hover:border-gray-400 hover:text-gray-800 bg-white')">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></svg>
            Resize
          </button>
          <button (click)="openUpscale()"
            [class]="'flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-[10px] sm:text-xs font-bold uppercase tracking-[0.1em] transition-all border ' + (isUpscaling ? 'bg-[#600b0b] text-white border-[#600b0b]' : 'border-gray-200 text-gray-600 hover:border-gray-400 hover:text-gray-800 bg-white')">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
            Upscale 2&times;
          </button>
        </div>

        <!-- ═══ UPSCALE PANEL ═══ -->
        <div *ngIf="isUpscaling" class="border border-gray-200 rounded-xl p-4 sm:p-5">
          <div class="flex items-center gap-2 mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-[#600b0b]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
            <h3 class="text-xs font-bold uppercase tracking-[0.1em] text-gray-800">Upscale Image</h3>
          </div>

          <div class="grid grid-cols-2 gap-3 mb-3">
            <div class="border border-gray-200 rounded-lg p-3 text-center">
              <p class="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-1">Current</p>
              <p class="text-base sm:text-lg font-bold text-gray-800">{{ imageInfo?.width }} <span class="text-gray-400 font-normal">&times;</span> {{ imageInfo?.height }}</p>
              <p class="text-[9px] text-gray-400">px</p>
            </div>
            <div class="border-2 border-[#600b0b]/20 bg-[#600b0b]/5 rounded-lg p-3 text-center">
              <p class="text-[9px] font-bold uppercase tracking-wider text-[#600b0b] mb-1">After 2&times; Upscale</p>
              <p class="text-base sm:text-lg font-bold text-[#600b0b]">{{ upscaledWidth }} <span class="font-normal">&times;</span> {{ upscaledHeight }}</p>
              <p class="text-[9px] text-[#600b0b]/60">px</p>
            </div>
          </div>

          <p class="text-[10px] text-gray-400 mb-3">
            Uses high-quality bicubic interpolation. Doubles pixel count for larger prints. Best used when original image is sharp.
          </p>

          <div class="flex gap-2">
            <button (click)="applyUpscale()"
              class="flex-1 flex items-center justify-center gap-1.5 bg-[#600b0b] text-white py-2.5 rounded-lg font-bold uppercase tracking-[0.1em] text-[10px] sm:text-xs hover:bg-[#8c1c1c] transition-all shadow">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
              Upscale 2&times;
            </button>
            <button (click)="cancelUpscale()"
              class="px-5 py-2.5 border border-gray-300 rounded-lg text-[10px] sm:text-xs font-bold uppercase tracking-[0.1em] text-gray-600 hover:border-gray-500 transition-all bg-white">
              Cancel
            </button>
          </div>
        </div>

        <!-- ═══ RESIZE PANEL ═══ -->
        <div *ngIf="isResizing" class="border border-gray-200 rounded-xl p-4 sm:p-5">
          <div class="flex items-center gap-2 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-[#600b0b]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></svg>
            <h3 class="text-xs font-bold uppercase tracking-[0.1em] text-gray-800">Resize Image</h3>
          </div>

          <p class="text-[10px] text-gray-500 mb-3">
            Current: {{ imageInfo?.width }} &times; {{ imageInfo?.height }} px
          </p>

          <div class="flex items-end gap-2 mb-2">
            <div class="flex-1">
              <label class="block text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-1">Width (px)</label>
              <input type="number" [(ngModel)]="resizeWidth" (ngModelChange)="onResizeWidthChange()" min="1"
                class="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-[#600b0b] focus:ring-1 focus:ring-[#600b0b]/20 transition-all" />
            </div>

            <button (click)="toggleResizeLock()" class="w-9 h-9 flex items-center justify-center mb-0.5 rounded-lg border border-gray-200 hover:border-gray-400 transition-all shrink-0"
              [class.text-[#600b0b]]="resizeLockAspect" [class.text-gray-400]="!resizeLockAspect">
              <svg *ngIf="resizeLockAspect" xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <svg *ngIf="!resizeLockAspect" xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>
            </button>

            <div class="flex-1">
              <label class="block text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-1">Height (px)</label>
              <input type="number" [(ngModel)]="resizeHeight" (ngModelChange)="onResizeHeightChange()" min="1"
                class="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-[#600b0b] focus:ring-1 focus:ring-[#600b0b]/20 transition-all" />
            </div>
          </div>

          <p class="text-[10px] text-gray-400 mb-3">
            New size: {{ resizeWidth }} &times; {{ resizeHeight }} px ({{ resizeSizeLabel }})
          </p>

          <div class="flex gap-2">
            <button (click)="applyResize()" [disabled]="resizeWidth < 1 || resizeHeight < 1"
              class="flex-1 flex items-center justify-center gap-1.5 bg-[#600b0b] text-white py-2.5 rounded-lg font-bold uppercase tracking-[0.1em] text-[10px] sm:text-xs hover:bg-[#8c1c1c] transition-all shadow disabled:opacity-40 disabled:cursor-not-allowed">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></svg>
              Apply Resize
            </button>
            <button (click)="cancelResize()"
              class="px-5 py-2.5 border border-gray-300 rounded-lg text-[10px] sm:text-xs font-bold uppercase tracking-[0.1em] text-gray-600 hover:border-gray-500 transition-all bg-white">
              Cancel
            </button>
          </div>
        </div>

      </div>

      <!-- ═══ RIGHT COLUMN ═══ -->
      <div class="flex flex-col gap-4 sm:gap-5">

        <!-- Wall Dimensions -->
        <div class="border border-gray-200 rounded-xl p-4 sm:p-5">
          <div class="flex items-center gap-2 mb-3 sm:mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-[#600b0b]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M21 3H3v18h18V3zM9 3v18M15 3v18M3 9h18M3 15h18"/>
            </svg>
            <h3 class="text-xs font-bold uppercase tracking-[0.1em] text-gray-800">Wall Dimensions</h3>
          </div>

          <div class="grid grid-cols-3 gap-3">
            <div>
              <label class="block text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-1">Width</label>
              <input type="number" [(ngModel)]="wallWidth" (ngModelChange)="onDimensionChange()" placeholder="Width" min="0" step="0.1"
                class="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm text-gray-700 focus:outline-none focus:border-[#600b0b] focus:ring-1 focus:ring-[#600b0b]/20 transition-all" />
            </div>
            <div>
              <label class="block text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-1">Height</label>
              <input type="number" [(ngModel)]="wallHeight" (ngModelChange)="onDimensionChange()" placeholder="Height" min="0" step="0.1"
                class="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm text-gray-700 focus:outline-none focus:border-[#600b0b] focus:ring-1 focus:ring-[#600b0b]/20 transition-all" />
            </div>
            <div>
              <label class="block text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-1">Unit</label>
              <select [(ngModel)]="unit" (ngModelChange)="onDimensionChange()"
                class="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:border-[#600b0b] focus:ring-1 focus:ring-[#600b0b]/20 transition-all appearance-none cursor-pointer"
                style="background-image: url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23999%22 stroke-width=%222%22><polyline points=%226 9 12 15 18 9%22/></svg>'); background-repeat: no-repeat; background-position: right 8px center;">
                <option value="Feet">Feet</option>
                <option value="Inches">Inches</option>
                <option value="Meters">Meters</option>
              </select>
            </div>
          </div>

          <p *ngIf="areaInSqFt > 0" class="text-[10px] text-gray-400 mt-2">
            Total area: {{ areaInSqFt | number:'1.1-1' }} sq.ft
          </p>
        </div>

        <!-- Quality Type -->
        <div class="border border-gray-200 rounded-xl p-4 sm:p-5">
          <div class="flex items-center gap-2 mb-3 sm:mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-[#600b0b]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <h3 class="text-xs font-bold uppercase tracking-[0.1em] text-gray-800">Quality</h3>
          </div>

          <div class="grid grid-cols-2 gap-2">
            <button (click)="qualityType = 'standard'; onQualityChange()"
              [class]="'py-2.5 px-3 rounded-lg border text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-all text-center ' + (qualityType === 'standard' ? 'border-[#600b0b] bg-[#600b0b] text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400 bg-white')">
              Standard
            </button>
            <button (click)="qualityType = 'premium'; onQualityChange()"
              [class]="'py-2.5 px-3 rounded-lg border text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-all text-center ' + (qualityType === 'premium' ? 'border-[#600b0b] bg-[#600b0b] text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400 bg-white')">
              Premium
            </button>
          </div>
        </div>

        <!-- Surface Type -->
        <div class="border border-gray-200 rounded-xl p-4 sm:p-5">
          <div class="flex items-center gap-2 mb-3 sm:mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-[#600b0b]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <path d="M3 9h18M9 3v18"/>
            </svg>
            <h3 class="text-xs font-bold uppercase tracking-[0.1em] text-gray-800">Surface Type</h3>
          </div>

          <div class="flex flex-wrap gap-2">
            <button *ngFor="let surface of surfaceTypes"
              (click)="selectSurface(surface)"
              [class]="'px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-semibold transition-all border ' + (selectedSurface.key === surface.key ? 'bg-[#600b0b] text-white border-[#600b0b]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400')">
              {{ surface.label }}
            </button>
          </div>

          <p class="text-[10px] text-gray-400 mt-2">
            {{ selectedSurface.label }} ({{ qualityType === 'premium' ? 'Premium' : 'Standard' }}):
            &#x20B9;{{ currentPricePerSqFt }} per sq. ft.
          </p>
        </div>

        <!-- Estimate -->
        <div *ngIf="areaInSqFt > 0" class="border border-gray-200 rounded-xl p-4 sm:p-5">
          <h3 class="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500 mb-2">Estimate</h3>
          <div class="flex justify-between items-center mb-1.5">
            <span class="text-xs text-gray-600">{{ areaInSqFt | number:'1.1-1' }} sq.ft &times; &#x20B9;{{ currentPricePerSqFt }}/sq.ft</span>
            <span class="text-xs font-semibold text-gray-800">&#x20B9;{{ totalPrice | number }}</span>
          </div>
          <div class="flex justify-between items-center pt-1.5 border-t border-gray-100">
            <span class="text-sm font-bold text-gray-900">Total</span>
            <span *ngIf="!priceLoading" class="text-xl font-bold text-[#600b0b]">&#x20B9;{{ totalPrice | number }}</span>
            <span *ngIf="priceLoading" class="text-xs text-gray-400">Calculating...</span>
          </div>
        </div>

        <!-- Submit Button -->
        <button [disabled]="!isFormValid"
          class="w-full bg-[#600b0b] text-white py-3 sm:py-3.5 rounded-lg font-bold uppercase tracking-[0.12em] text-[10px] sm:text-xs hover:bg-[#8c1c1c] transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
          Submit Design for Print
        </button>

        <p class="text-[10px] text-gray-400 text-center -mt-1.5">
          Our team will review and send a print preview within 24 hours.
        </p>

      </div>
    </div>

  </div>

</div>
